import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { BrandLockup } from "@/components/BrandMark";
import { Reveal } from "@/components/common";

export default function PasswordRecovery({ mode }) {
  const isReset = mode === "reset";
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setDone("");
    try {
      if (isReset) {
        if (!token) throw Object.assign(new Error("Falta el token del enlace"), { response: { data: { detail: "Enlace incompleto. Pide uno nuevo desde recuperar contraseña." } } });
        const { data } = await api.post("/auth/reset-password", { token, new_password: password });
        setDone(data.detail || "Contraseña actualizada.");
        setTimeout(() => nav("/login"), 1500);
      } else {
        const { data } = await api.post("/auth/forgot-password", { email });
        setDone(data.detail || "Revisa tu email.");
        if (data.debug_link) setDone(`${data.detail} (debug: ${data.debug_link})`);
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 py-10">
      <Reveal className="w-full max-w-sm">
        <BrandLockup useImage compact />
        <h1 className="mt-6 text-3xl font-extrabold" data-testid="recovery-title">
          {isReset ? "Nueva contraseña" : "¿Olvidaste tu contraseña?"}
        </h1>
        <p className="mt-2 text-sm text-stone-600">
          {isReset
            ? "Elige una contraseña nueva (mínimo 6 caracteres)."
            : "Te enviaremos un enlace a tu email para restablecerla. Caduca en 1 hora."}
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="recovery-form">
          {isReset ? (
            <Input
              required
              type="password"
              minLength={6}
              placeholder="Nueva contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 rounded-2xl"
              data-testid="recovery-password-input"
            />
          ) : (
            <Input
              required
              type="email"
              placeholder="Email de tu cuenta"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 rounded-2xl"
              data-testid="recovery-email-input"
            />
          )}
          {error && <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="recovery-error">{error}</p>}
          {done && <p className="rounded-2xl bg-food-soft px-4 py-3 text-sm text-food-ink" data-testid="recovery-done">{done}</p>}
          <button
            disabled={busy}
            className="w-full rounded-full bg-ink py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"
            data-testid="recovery-submit-btn"
          >
            {busy ? "Un momento…" : isReset ? "Guardar contraseña" : "Enviar enlace"}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-stone-600">
          <Link to="/login" className="font-semibold text-train-ink underline-offset-4 hover:underline" data-testid="recovery-login-link">
            Volver al login
          </Link>
        </p>
      </Reveal>
    </div>
  );
}
